import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to include credentials on all requests
axios.defaults.withCredentials = true;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

/** Safely convert any FastAPI error detail to a string for display */
export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Algo salió mal. Intenta de nuevo.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  // null = checking session, false = not authenticated, object = user
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(data);
    return data;
  };

  const register = async ({ email, password, name, phone, referral_code, pet_name, pet_birthday }) => {
    const { data } = await axios.post(
      `${API}/auth/register`,
      {
        email, password, name, phone,
        referral_code: referral_code || undefined,
        pet_name: pet_name || undefined,
        pet_birthday: pet_birthday || undefined,
      },
      { withCredentials: true }
    );
    setUser(data);
    return data;
  };

  const updatePet = async ({ pet_name, pet_birthday }) => {
    const { data } = await axios.put(`${API}/auth/me/pet`, { pet_name, pet_birthday }, { withCredentials: true });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // ignore
    }
    setUser(false);
  };

  const forgotPassword = async (email) => {
    const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
    return data;
  };

  const resetPassword = async (token, newPassword) => {
    const { data } = await axios.post(`${API}/auth/reset-password`, {
      token,
      new_password: newPassword
    });
    return data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        login,
        register,
        updatePet,
        logout,
        forgotPassword,
        resetPassword,
        refresh: checkSession
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
